import { useMemo } from 'react';

import { fetcher, queryString, useRecoilQuery } from '@shared/hooks/recoil-query';
import { LowerRole } from '@shared/types';
import { useQuery } from 'react-query';

import { CouponRecipientInfo, searchCouponRecipients } from './giftCoupon.controller';
import {
  giftCouponByCodeDataAtomFamily,
  giftCouponByCodeStatusAtomFamily,
  giftCouponByIdDataAtomFamily,
  giftCouponByIdStatusAtomFamily,
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
  giftCouponStatisticsDataAtom,
  giftCouponStatisticsStatusAtom,
  redemptionDashboardDataAtom,
  redemptionDashboardStatusAtom,
} from './giftCoupon.atom';
import { GiftCouponSearchParams } from './giftCoupon.interface';

// 코드로 쿠폰 조회 URL (public)
const giftCouponByCodeUrl = (code: string) => {
  return `/${LowerRole.Public}/gift-coupon/code/${code}`;
};

export const useGiftCouponByCodeHook = (code: string, skip = false) => {
  useRecoilQuery(
    giftCouponByCodeStatusAtomFamily(code),
    giftCouponByCodeDataAtomFamily(code),
    giftCouponByCodeUrl(code),
    fetcher,
    skip || !code,
  );
};

// ID로 쿠폰 조회 URL (public)
const giftCouponByIdUrl = (id: string) => {
  return `/${LowerRole.Public}/gift-coupon/${id}`;
};

export const useGiftCouponByIdHook = (id: string, skip = false) => {
  useRecoilQuery(
    giftCouponByIdStatusAtomFamily(id),
    giftCouponByIdDataAtomFamily(id),
    giftCouponByIdUrl(id),
    fetcher,
    skip || !id,
  );
};

const giftCouponPopulate = [
  {
    path: 'giftItem',
    populate: [
      { path: 'image' },
      {
        path: 'giftItemInfo',
        populate: [{ path: 'image' }, { path: 'giftItemBrand' }],
      },
    ],
  },
  {
    path: 'selectedChoiceUnit',
    populate: [{ path: 'image' }, { path: 'giftItemBrand' }],
  },
  {
    path: 'giftOrder',
    select: 'paymentDetail.purchasedAt bugo',
    populate: [
      {
        path: 'bugo',
        select: 'member',
        populate: [{ path: 'member', select: 'fullName phoneNumber' }],
      },
    ],
  },
  { path: 'recipient.signatureImage' },
  // 카카오톡 공유 웹훅 역참조 — 쿠폰내역검색 페이지의 카톡공유기록 모달용
  {
    path: 'kakaoShareCallbacks',
    select: 'chatType hashChatId args appName kakaoResourceId createdAt',
    options: { sort: { createdAt: -1 } },
  },
];

// 발송대기 대시보드 조회 URL (admin)
export const redemptionDashboardUrl = `/${LowerRole.Admin}/gift-coupon/redemption-dashboard`;

export const useRedemptionDashboardHook = (skip = false) => {
  useRecoilQuery(
    redemptionDashboardStatusAtom,
    redemptionDashboardDataAtom,
    redemptionDashboardUrl,
    fetcher,
    skip,
  );
};

// 쿠폰 통계 조회 URL (admin)
export const giftCouponStatisticsUrl = `/${LowerRole.Admin}/gift-coupon/statistics`;

export const useGiftCouponStatisticsHook = (skip = false) => {
  useRecoilQuery(
    giftCouponStatisticsStatusAtom,
    giftCouponStatisticsDataAtom,
    giftCouponStatisticsUrl,
    fetcher,
    skip,
  );
};

// 쿠폰 검색 URL (admin) - 내역 검색 페이지용
const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const giftCouponSearchUrl = (params: GiftCouponSearchParams) => {
  const filter: Record<string, unknown> = {};

  if (params.redeemStatus) {
    if (Array.isArray(params.redeemStatus)) {
      filter.redeemStatus =
        params.redeemStatus.length === 1
          ? params.redeemStatus[0]
          : { $in: params.redeemStatus };
    } else {
      filter.redeemStatus = params.redeemStatus;
    }
  }

  if (params.shareStatus) {
    filter.shareStatus = params.shareStatus;
  }

  if (params.phoneNumber) {
    filter['recipient.phoneNumber'] = { $regex: params.phoneNumber };
  }

  if (params.fullCode) {
    filter.code = { $regex: params.fullCode, $options: 'i' };
  }

  // 통합 번호 검색 — 어느 번호로 들어왔는지 모를 때 세 필드를 한 번에 훑는다.
  if (params.anyNumber) {
    const raw = params.anyNumber.trim();
    const digits = raw.replace(/[^0-9]/g, '');
    const or: Record<string, unknown>[] = [];
    if (digits) {
      or.push({ 'recipient.phoneNumber': { $regex: digits } });
      or.push({ additionalRecipientPhone: { $regex: digits } });
    }
    // 쿠폰코드는 영문 포함이라 원문으로 매칭 (정규식 메타문자는 이스케이프)
    or.push({ code: { $regex: escapeRegExp(raw), $options: 'i' } });
    filter.$or = or;
  }

  if (params.dateFrom || params.dateTo) {
    const dateFilter: Record<string, string> = {};
    if (params.dateFrom) {
      dateFilter.$gte = new Date(`${params.dateFrom}T00:00:00+09:00`).toISOString();
    }
    if (params.dateTo) {
      dateFilter.$lte = new Date(`${params.dateTo}T23:59:59.999+09:00`).toISOString();
    }
    const field =
      params.dateField === 'deliveredAt'
        ? 'gifticon.deliveredAt'
        : 'recipient.requestedAt';
    filter[field] = dateFilter;
  }

  return `/${LowerRole.Admin}/gift-coupon/search?${queryString({
    filter,
    populate: giftCouponPopulate,
    options: { sort: { createdAt: -1 } },
    ...(params.ordererName ? { ordererName: params.ordererName } : {}),
    ...(params.ordererPhone ? { ordererPhone: params.ordererPhone } : {}),
  })}`;
};

export const useGiftCouponSearchHook = (
  searchKey: string,
  params: GiftCouponSearchParams,
  skip = false,
) => {
  useRecoilQuery(
    giftCouponSearchStatusAtomFamily(searchKey),
    giftCouponSearchDataAtomFamily(searchKey),
    giftCouponSearchUrl(params),
    fetcher,
    skip,
  );
};

/**
 * 쿠폰 id → 교환신청자(수령자) 맵. CS 응대 로그처럼 요약에 수령자가 안 실리는 화면 보강용.
 *
 * - 서버 요약이 수령자를 내려주기 시작하면 호출부가 부족한 id만 넘기므로 자연히 요청이 사라진다.
 * - 부가 정보라 실패해도 화면을 막지 않는다 (빈 맵으로 흡수, 재시도 1회).
 */
export const useCouponRecipientsQuery = (couponIds: string[], enabled = true) => {
  const ids = useMemo(
    () => Array.from(new Set(couponIds.filter(Boolean))).sort(),
    [couponIds],
  );

  return useQuery<Record<string, CouponRecipientInfo>>(
    ['gift-coupon', 'recipients', ids.join(',')],
    async () => {
      try {
        return await searchCouponRecipients(ids);
      } catch (e) {
        //* 부가 정보라 화면은 막지 않되, 조용히 비는 건 사고다 — 콘솔에는 남긴다
        // eslint-disable-next-line no-console
        console.warn('[csMemoLog] 쿠폰 수령자 보강 조회 실패 (수령자 열이 빕니다)', e);
        return {};
      }
    },
    {
      enabled: enabled && ids.length > 0,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
};

/**
 * 수령자 **상세** (인증 기록·서명) — 모달을 열었을 때 그 쿠폰 한 건만.
 * 목록 보강(useCouponRecipientsQuery)에서 이 둘을 뺀 짝이다: 서명은 ref라 populate가 붙어
 * 목록 100건에 얹으면 화면 진입마다 그 비용을 치르는데, 정작 보는 건 눌러본 한 건뿐이다.
 */
export const useCouponRecipientDetailQuery = (couponId: string | undefined) =>
  useQuery<CouponRecipientInfo | null>(
    ['gift-coupon', 'recipient-detail', couponId ?? ''],
    async () => {
      if (!couponId) return null;
      try {
        const map = await searchCouponRecipients([couponId], { detail: true });
        return map[couponId] ?? null;
      } catch (e) {
        //* 부가 정보라 모달은 뜨게 두되, 조용히 비는 건 사고다 — 콘솔에는 남긴다
        // eslint-disable-next-line no-console
        console.warn('[csMemoLog] 쿠폰 수령자 상세 조회 실패 (인증·서명이 빕니다)', e);
        return null;
      }
    },
    {
      enabled: !!couponId,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  );
